// useComposerStore — shared store for the prospect-detail Composer.
//
// α.6 of the outreach-agent slice. Lifts subject + body out of
// ProspectDetail's local React state so the chat dock (a sibling subtree)
// can populate the Composer when the founder clicks "Use this draft" on
// an agent-emitted `:::widget[draft-card]:::`.
//
// Why zustand (vs. React Context + useReducer):
//   - The store is read by ComposerSection and written by DraftCard, which
//     live in disjoint React subtrees mounted by different routes (the
//     dock is in the cockpit shell; ComposerSection is in /outreach/
//     prospects/:id). A shared Context provider would have to wrap a
//     higher ancestor than either; zustand sidesteps the provider plumbing
//     and gives us selector subscriptions for free (so each Composer
//     field only re-renders on its own slice change).
//   - The store stays tiny (5 fields, 4 actions) — no need for slices,
//     middleware, or persistence. If the surface grows beyond this, the
//     mid-flight refactor cost is low.
//
// pendingDraftId is the link back to the source draft for the (future)
// `outreach.send.*` event chain — when the founder sends, we want to
// reference the draftId in the projector event so we can audit which
// agent-saved draft actually shipped. Today it's stored but not yet
// consumed; α.7 wires it through SendConfirmModal → sendOutreach.

import { create } from 'zustand';

interface ComposerState {
  subject: string;
  body: string;
  pendingDraftId: string | null;
  setSubject: (s: string) => void;
  setBody: (s: string) => void;
  loadDraft: (d: { draftId: string; subject?: string; body: string }) => void;
  clear: () => void;
}

export const useComposerStore = create<ComposerState>((set) => ({
  subject: '',
  body: '',
  pendingDraftId: null,
  setSubject: (s) => set({ subject: s }),
  setBody: (s) => set({ body: s }),
  loadDraft: (d) =>
    set({
      subject: d.subject ?? '',
      body: d.body,
      pendingDraftId: d.draftId,
    }),
  clear: () => set({ subject: '', body: '', pendingDraftId: null }),
}));
